import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import { observer } from 'mobx-react-lite';
import { ClientItemId } from 'owa-client-ids';
import loc, { format } from 'owa-localize';
import { getReactionResourceData, ReactionsMap } from 'owa-mail-reactions-data';
import * as lazyTriageActions from 'owa-mail-triage-action';
import PersonaControl from 'owa-persona/lib/components/PersonaControl';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { InstrumentationContext } from 'owa-search/lib/types/InstrumentationContext';
import Message from 'owa-service/lib/contract/Message';
import ReactionsGroupedByType from 'owa-service/lib/contract/ReactionsGroupedByType';
import * as React from 'react';
import {
    useLivePersonaCard,
    PersonaCardBehaviorProps,
} from 'owa-persona/lib/components/PersonaCardBehavior';
import {
    Reaction_ReactorsReaction_Title,
    Reaction_SetAnnouncement_Text,
    Reaction_UnsetAnnouncement_Text,
} from '../utils/reactions.locstring.json';

const styles = require('./Reactions.scss');
const classNames = require('classnames');

export interface ReactionInfoButtonProps {
    ownerReactionType: string;
    reactionInfo: ReactionsGroupedByType;
    conversationId: ClientItemId;
    itemId: ClientItemId;
    message: Message;
    instrumentationContext: InstrumentationContext;
    isInCollapsedRP?: boolean;
}

export const ReactionInfoButton = observer(function ReactionInfoButton(
    props: ReactionInfoButtonProps
) {
    const divRef = React.useRef<HTMLDivElement>();
    let showTimeout: ReturnType<typeof setTimeout> = null;
    let hideTimeout: ReturnType<typeof setTimeout> = null;
    const [isCalloutVisible, showCallout, hideCallout] = useBooleanState(false);

    const resourceData = getReactionResourceData(
        props.reactionInfo.ReactionType,
        false /* isAnimated */
    );

    const onReaction = (event?: React.MouseEvent<{}>) => {
        const announcementString = format(
            props.ownerReactionType === props.reactionInfo.ReactionType
                ? loc(Reaction_UnsetAnnouncement_Text)
                : loc(Reaction_SetAnnouncement_Text),
            ReactionsMap[props.reactionInfo.ReactionType].localizedName
        );

        lazyTriageActions.lazyReactToItem.importAndExecute(
            props.message,
            props.itemId,
            props.conversationId,
            props.reactionInfo.ReactionType,
            props.instrumentationContext,
            announcementString,
            props.ownerReactionType
        );

        event.stopPropagation();
        event.preventDefault();
    };

    const reactorsList = (): JSX.Element => {
        return (
            <FocusZone direction={FocusZoneDirection.vertical}>
                <div className={classNames(styles.scrollContainer, 'customScrollBar')}>
                    <div className={styles.reactionTitle}>
                        {resourceData && (
                            <img className={styles.reactionTitleIcon} src={resourceData} />
                        )}
                        {!resourceData && (
                            <span className={styles.reactionTitleIcon}>
                                {ReactionsMap[props.reactionInfo.ReactionType].icon}
                            </span>
                        )}
                        <div className={styles.reactionLabel}>
                            {format(
                                loc(Reaction_ReactorsReaction_Title),
                                ReactionsMap[props.reactionInfo.ReactionType].localizedName
                            )}
                        </div>
                    </div>

                    <div className={styles.personaCollection}>
                        {props.reactionInfo.Reactors.map(reactor => {
                            const emailAddress = reactor.ReactorEmail.EmailAddress;
                            return (
                                <LPCWrapper
                                    key={emailAddress}
                                    emailAddress={emailAddress}
                                    name={reactor.ReactorEmail.Name}
                                    mailBoxType={reactor.ReactorEmail.MailboxType}
                                    disableHover={true}
                                    clientScenario="Reactor">
                                    <PersonaControl
                                        className={styles.persona}
                                        key={emailAddress}
                                        name={reactor.ReactorEmail.Name}
                                        emailAddress={emailAddress}
                                        size={PersonaSize.size32}
                                        showPersonaDetails={true}
                                        mailboxType={reactor.ReactorEmail.MailboxType}
                                    />
                                </LPCWrapper>
                            );
                        })}
                    </div>
                </div>
            </FocusZone>
        );
    };

    const onMouseEnter = () => {
        clearTimeout(showTimeout);
        clearTimeout(hideTimeout);

        showTimeout = setTimeout(() => {
            showCallout();
        }, 300);
    };
    const onMouseLeave = () => {
        clearTimeout(showTimeout);
        clearTimeout(hideTimeout);

        hideTimeout = setTimeout(() => {
            dismissCallout();
        }, 150);
    };
    const dismissCallout = () => {
        hideCallout();
        setTimeout(() => {
            divRef?.current?.focus();
        }, 0);
    };

    const buttonStyle = classNames([
        styles.reactionInfoButton,
        props.isInCollapsedRP && styles.reactionInfoButtonCollapsedRP,
        props.reactionInfo.ReactionType === props.ownerReactionType &&
            styles.reactionInfoButtonOwnerReacted,
    ]);

    // If the reaction type is not on the map, show nothing
    if (!ReactionsMap[props.reactionInfo.ReactionType]) {
        return null;
    }

    return (
        <>
            <div
                ref={divRef}
                data-is-focusable={true}
                className={buttonStyle}
                onClick={props.isInCollapsedRP ? null : onReaction}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}>
                {resourceData && <img className={styles.reactionInfoIcon} src={resourceData} />}
                {!resourceData && (
                    <span className={styles.reactionInfoIcon}>
                        {ReactionsMap[props.reactionInfo.ReactionType].icon}
                    </span>
                )}
                <span
                    className={
                        props.isInCollapsedRP
                            ? styles.reactionInfoLabelCollapsedRP
                            : styles.reactionInfoLabel
                    }>
                    {props.reactionInfo.Count}
                </span>
            </div>
            <Callout
                target={divRef}
                hidden={!isCalloutVisible}
                isBeakVisible={false}
                setInitialFocus={true}
                gapSpace={4}
                directionalHint={DirectionalHint.topCenter}
                onDismiss={dismissCallout}>
                <div
                    className={styles.reactionsCalloutContainer}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}>
                    {reactorsList()}
                </div>
            </Callout>
        </>
    );
});

function LPCWrapper(props: PersonaCardBehaviorProps & { children: React.ReactNode }) {
    const PersonaCardBehavior = useLivePersonaCard(props);
    return <PersonaCardBehavior>{props.children}</PersonaCardBehavior>;
}
